import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { MutedText, SmallText } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

export const ALIGNMENT_BUTTON_CLASS = 'h-8 w-10 px-0';
export const ALIGNMENT_TOOLTIP_CONTENT_PROPS = { className: 'z-[140]' };

export type OptionButtonOption<T extends string> = {
  value: T;
  label: string;
  ariaLabel?: string;
  disabled?: boolean;
};

interface SettingRowProps {
  title: string;
  description: string;
  children: ReactNode;
  className?: string;
  controlClassName?: string;
}

export function SettingRow({
  title,
  description,
  children,
  className,
  controlClassName,
}: SettingRowProps) {
  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      <div className="max-w-[14.5rem] space-y-0.5">
        <SmallText className="leading-5 font-medium text-foreground">{title}</SmallText>
        <MutedText className="text-xs leading-5">{description}</MutedText>
      </div>
      <div className={cn('shrink-0', controlClassName)}>{children}</div>
    </div>
  );
}

interface OptionButtonGroupProps<T extends string> {
  options: OptionButtonOption<T>[];
  value: T;
  onChange: (nextValue: T) => void;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
}

export function OptionButtonGroup<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  className,
  buttonClassName,
}: OptionButtonGroupProps<T>) {
  return (
    <ButtonGroup className={className}>
      {options.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'solid' : 'outline'}
          colorScheme="secondary"
          className={cn('h-8 flex-1 px-1 text-[11px]', buttonClassName)}
          aria-label={option.ariaLabel}
          onClick={() => onChange(option.value)}
          disabled={disabled || option.disabled}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  );
}
